import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Modal from '../Components/Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { useAuth } from '../AuthContext';

const POKEAPI = 'https://pokeapi.co/api/v2';

function spriteUrl(pokemonId) {
  return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/shiny/${pokemonId}.png`;
}

function smallSprite(pokemonId) {
  return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${pokemonId}.png`;
}

function idFromUrl(url) {
  return parseInt(url.replace(/\/$/, '').split('/').pop(), 10);
}

let cachedPokeList = null;

function PokemonShinies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shinies, setShinies] = useState([]);
  const [pokeList, setPokeList] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addSelected, setAddSelected] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addStatus, setAddStatus] = useState('');
  const [editMode, setEditMode] = useState(false);

  const [editingShiny, setEditingShiny] = useState(null);
  const [editName, setEditName] = useState('');
  const [editSelected, setEditSelected] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');

  const fetchShinies = async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/shinies?order=created_at.desc&select=*`, {
      headers: supabaseHeaders(false),
    });
    const data = await res.json();
    setShinies(Array.isArray(data) ? data : []);
  };

  useEffect(() => { fetchShinies(); }, []);

  useEffect(() => {
    if (cachedPokeList) { setPokeList(cachedPokeList); return; }
    fetch(`${POKEAPI}/pokemon?limit=100000&offset=0`)
      .then(r => r.json())
      .then(data => {
        cachedPokeList = data.results || [];
        setPokeList(cachedPokeList);
      });
  }, []);

  const suggestions = addName.length >= 2
    ? pokeList.filter(p => p.name.includes(addName.toLowerCase().trim())).slice(0, 8)
    : [];

  const editSuggestions = editName.length >= 2
    ? pokeList.filter(p => p.name.includes(editName.toLowerCase().trim())).slice(0, 8)
    : [];

  const openAdd = () => {
    setAddName('');
    setAddSelected(null);
    setAddStatus('');
    setShowAdd(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addSelected) { setAddStatus('Selecteer een Pokémon.'); return; }
    setAddLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/shinies`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify({ name: addSelected.name, pokemon_id: addSelected.pokemonId }),
      });
      if (!res.ok) throw new Error('Failed to add');
      setAddStatus('Added!');
      setTimeout(() => { setShowAdd(false); fetchShinies(); }, 800);
    } catch (err) {
      setAddStatus(`Something went wrong: ${err.message}`);
    } finally {
      setAddLoading(false);
    }
  };

  const openEdit = (p) => {
    setEditingShiny(p);
    setEditName(p.name);
    setEditSelected({ name: p.name, pokemonId: p.pokemon_id });
    setEditStatus('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editSelected) { setEditStatus('Selecteer een Pokémon.'); return; }
    setEditLoading(true);
    setEditStatus('');
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/shinies?id=eq.${editingShiny.id}`, {
        method: 'PATCH',
        headers: supabaseHeaders(true),
        body: JSON.stringify({ name: editSelected.name, pokemon_id: editSelected.pokemonId }),
      });
      if (!res.ok) throw new Error('Update failed');
      setEditStatus('Saved!');
      setTimeout(() => { setEditingShiny(null); fetchShinies(); }, 800);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditDelete = async () => {
    if (!window.confirm(`Delete "${editingShiny.name}"?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/shinies?id=eq.${editingShiny.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      setEditingShiny(null);
      fetchShinies();
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    }
  };

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-title-row">
          <h1>Shinies</h1>
          <Link to="/pokemon/shinydex" className="header-nav-btn">Shiny Dex</Link>
        </div>
        <div className="controls">
          <button onClick={() => navigate('/pokemon')} className="header-nav-btn">←</button>
          {user && <button onClick={openAdd}>Add +</button>}
          {user && (
            <button onClick={() => setEditMode((m) => !m)} className={editMode ? 'btn-active' : ''}>
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="todo-grid">
        {shinies.map((p, idx) => (
          <div
            className="todo-card"
            key={p.id}
            style={{ '--index': idx, cursor: editMode ? 'pointer' : 'default' }}
            onClick={editMode ? () => openEdit(p) : undefined}
          >
            <img
              src={spriteUrl(p.pokemon_id)}
              alt={p.name}
              className="todo-card-poster todo-card-poster--pokemon"
              onError={(e) => { e.target.onerror = null; e.target.src = smallSprite(p.pokemon_id); }}
            />
            <div className="todo-card-title" style={{ textTransform: 'capitalize' }}>{p.name}</div>
          </div>
        ))}
      </div>

      {shinies.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <h2 className="modal-title">Add Shiny</h2>
          <form className="modal-form" onSubmit={handleAdd}>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={addName}
                onChange={(e) => { setAddName(e.target.value); setAddSelected(null); }}
                
                autoFocus
                autoComplete="off"
              />
            </div>

            {suggestions.length > 0 && !addSelected && (
              <div className="pokemon-search-results">
                {suggestions.map((s) => {
                  const pid = idFromUrl(s.url);
                  return (
                    <div
                      key={s.name}
                      className="pokemon-search-result"
                      onClick={() => { setAddSelected({ name: s.name, pokemonId: pid }); setAddName(s.name); }}
                    >
                      <img src={smallSprite(pid)} alt={s.name} className="pokemon-search-img" />
                      <div className="pokemon-search-info">
                        <strong style={{ textTransform: 'capitalize' }}>{s.name}</strong>
                        <span>#{String(pid).padStart(3, '0')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {addSelected && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <img src={spriteUrl(addSelected.pokemonId)} alt={addSelected.name} style={{ width: 140 }} />
                <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                  {addSelected.name} #{String(addSelected.pokemonId).padStart(3, '0')}
                </span>
              </div>
            )}

            {addStatus && <p className="form-status">{addStatus}</p>}
            <div className="edit-modal-actions">
              <button type="submit" className="form-submit" disabled={addLoading || !addSelected}>
                {addLoading ? 'Working...' : 'Add'}
              </button>
              <button type="button" className="form-submit btn-cancel" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editingShiny && (
        <Modal onClose={() => setEditingShiny(null)}>
          <h2 className="modal-title">Edit Shiny</h2>
          <form className="modal-form" onSubmit={handleEditSubmit}>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => { setEditName(e.target.value); setEditSelected(null); }}
                autoFocus
                autoComplete="off"
              />
            </div>

            {editSuggestions.length > 0 && !editSelected && (
              <div className="pokemon-search-results">
                {editSuggestions.map((s) => {
                  const pid = idFromUrl(s.url);
                  return (
                    <div
                      key={s.name}
                      className="pokemon-search-result"
                      onClick={() => { setEditSelected({ name: s.name, pokemonId: pid }); setEditName(s.name); }}
                    >
                      <img src={smallSprite(pid)} alt={s.name} className="pokemon-search-img" />
                      <div className="pokemon-search-info">
                        <strong style={{ textTransform: 'capitalize' }}>{s.name}</strong>
                        <span>#{String(pid).padStart(3, '0')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {editSelected && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <img src={spriteUrl(editSelected.pokemonId)} alt={editSelected.name} style={{ width: 140 }} />
                <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                  {editSelected.name} #{String(editSelected.pokemonId).padStart(3, '0')}
                </span>
              </div>
            )}

            {editStatus && <p className="form-status">{editStatus}</p>}
            <div className="edit-modal-actions">
              <button type="button" className="form-submit btn-cancel" onClick={() => setEditingShiny(null)}>
                Cancel
              </button>
              <button type="button" className="form-submit btn-delete" onClick={handleEditDelete}>
                Delete
              </button>
              <button type="submit" className="form-submit" disabled={editLoading || !editSelected}>
                {editLoading ? 'Working...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default PokemonShinies;
